import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Edit2, FileText, Clock, AlertTriangle, Scale, CheckSquare,
  ChevronDown, ChevronUp, Upload, X, Plus, Save, Trash2,
} from 'lucide-react'
import {
  getCaseById,
  getDocumentsByCaseId,
  getNodesByCaseId,
  getTimelineEventsByCaseId,
  getAnalysisByCaseAndPerspective,
  createDocument,
  updateNodeStatus,
  createTimelineEvent,
  uploadFile,
} from '../lib/api'
import type { Case, Document, Node, TimelineEvent, AnalysisReport } from '../types'

export default function CaseDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [caseData, setCaseData] = useState<Case | null>(null)
  const [documents, setDocuments] = useState<Document[]>([])
  const [nodes, setNodes] = useState<Node[]>([])
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [analysis, setAnalysis] = useState<AnalysisReport | null>(null)
  const [activeTab, setActiveTab] = useState('overview')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDocModal, setShowDocModal] = useState(false)
  const [newDoc, setNewDoc] = useState({ type: '', date: '', content: '' })
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (id) {
      loadData(id)
    } else {
      navigate('/cases')
    }
  }, [id])

  const loadData = async (caseId: string) => {
    try {
      setLoading(true)
      const [caseResult, docs, nodesList, timelineEvents, analysisResult] = await Promise.all([
        getCaseById(caseId).catch(() => null),
        getDocumentsByCaseId(caseId).catch(() => []),
        getNodesByCaseId(caseId).catch(() => []),
        getTimelineEventsByCaseId(caseId).catch(() => []),
        getAnalysisByCaseAndPerspective(caseId, 'plaintiff').catch(() => null),
      ])
      setCaseData(caseResult)
      setDocuments(docs || [])
      setNodes(nodesList || [])
      setTimeline(timelineEvents || [])
      setAnalysis(analysisResult)
      
      // 如果案件不存在，显示提示
      if (!caseResult) {
        setError('案件不存在或已被删除')
      } else {
        setError(null)
      }
    } catch (err) {
      console.error('加载案件详情失败:', err)
      setError('加载数据失败，请刷新重试')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateDocument = async () => {
    if (!id || !newDoc.type || !newDoc.date) return
    try {
      const doc = await createDocument({
        case_id: id,
        doc_type: newDoc.type,
        doc_date: newDoc.date,
        received_date: newDoc.date,
        source: 'our_side',
        content: newDoc.content,
        file_url: null,
        ocr_text: null,
        ocr_confidence: null,
        metadata: {},
      })
      setDocuments(prev => [doc, ...prev])
      setShowDocModal(false)
      setNewDoc({ type: '', date: '', content: '' })
    } catch (err) {
      alert('创建失败，请重试')
    }
  }

  const handleFileUpload = async (file: File) => {
    if (!id) return
    try {
      setUploading(true)
      const url = await uploadFile(file, id)
      const doc = await createDocument({
        case_id: id,
        doc_type: file.name.split('.')[0],
        doc_date: new Date().toISOString().split('T')[0],
        received_date: new Date().toISOString().split('T')[0],
        source: 'our_side',
        content: null,
        file_url: url,
        ocr_text: null,
        ocr_confidence: null,
        metadata: { filename: file.name },
      })
      setDocuments(prev => [doc, ...prev])
    } catch (err) {
      alert('上传失败，请重试')
    } finally {
      setUploading(false)
    }
  }

  const handleNodeComplete = async (nodeId: string) => {
    await updateNodeStatus(nodeId, 'completed')
    loadData(id!)
  }

  const handleTimelineEvent = async () => {
    if (!id || !newDoc.type) return
    try {
      const event: TimelineEvent = {
        id: '',
        case_id: id,
        event_date: newDoc.date,
        event_type: 'action_taken',
        title: newDoc.type,
        description: newDoc.content,
        related_doc_id: null,
        related_node_id: null,
        created_at: new Date().toISOString(),
      }
      const created = await createTimelineEvent(event)
      setTimeline(prev => [created, ...prev])
      setShowDocModal(false)
      setNewDoc({ type: '', date: '', content: '' })
    } catch (err) {
      alert('添加失败，请重试')
    }
  }

  if (loading) {
    return <div className="text-center py-8 text-gray-500">加载中...</div>
  }

  if (error || !caseData) {
    return (
      <div className="text-center py-8 text-red-500">
        <p>{error || '案件不存在'}</p>
        <button onClick={() => navigate('/cases')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg">
          返回列表
        </button>
      </div>
    )
  }

  const tabs = [
    { id: 'overview', label: '总览' },
    { id: 'documents', label: '文书管理' },
    { id: 'timeline', label: '时间线' },
    { id: 'nodes', label: '程序节点' },
    { id: 'analysis', label: '角度分析' },
  ]

  const statusColor = (status: string) => {
    const map: Record<string, string> = {
      completed: 'bg-green-100 text-green-700 border-green-300',
      active: 'bg-blue-100 text-blue-700 border-blue-300',
      expiring: 'bg-yellow-100 text-yellow-700 border-yellow-300',
      upcoming: 'bg-gray-100 text-gray-600 border-gray-300',
      expired: 'bg-red-100 text-red-700 border-red-300',
    }
    return map[status] || map.upcoming
  }

  const priorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return '🔴'
      case 'high': return '🟠'
      case 'medium': return '🟡'
      case 'low': return '🟢'
      default: return ''
    }
  }

  const caseStatusLabel = (status: string) => {
    const map: Record<string, string> = {
      pending: '待发起',
      active: '进行中',
      closed: '已结案',
      suspended: '中止',
    }
    return map[status] || status
  }

  const caseTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      civil_litigation: '民事诉讼',
      trademark_admin: '商标行政诉讼',
      other: '其他',
    }
    return map[type] || type
  }

  const partyRoleLabel = (role: string) => {
    const map: Record<string, string> = {
      plaintiff: '原告',
      defendant: '被告',
      third_party: '第三人',
    }
    return map[role] || role
  }

  return (
    <div className="space-y-4">
      {/* Breadcrumb & Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/cases')} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-lg font-bold">{caseData.case_number || caseData.title}</h1>
            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
              {caseStatusLabel(caseData.status)}
            </span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {caseTypeLabel(caseData.case_type)}
            </span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
              {partyRoleLabel(caseData.party_role)}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 mt-2 text-xs text-gray-600">
            <span><span className="text-gray-400">案由：</span>{caseData.title}</span>
            <span><span className="text-gray-400">法院：</span>{caseData.court || '—'}</span>
            <span><span className="text-gray-400">对方当事人：</span>{caseData.opposing_party}</span>
            <span><span className="text-gray-400">标的金额：</span><span className="font-semibold text-indigo-600">{caseData.claim_amount ? `¥${(caseData.claim_amount / 10000).toFixed(0)}万` : '—'}</span></span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/cases/${id}/edit`)}
          className="btn btn-outline text-sm flex items-center gap-2"
        >
          <Edit2 size={14} /> 编辑案件
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.id
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <h3 className="font-semibold text-sm mb-3">关键期限</h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-500">节点</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">截止日期</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-500">状态</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-500">备注</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nodes.slice(-3).map(n => (
                        <tr key={n.id} className="border-t border-gray-100">
                          <td className="py-2 px-3">
                            <span className="mr-1">{priorityIcon(n.priority)}</span>
                            {n.node_name}
                          </td>
                          <td className={`py-2 px-3 font-medium ${n.status === 'active' ? 'text-red-600' : 'text-gray-700'}`}>
                            {n.end_date}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor(n.status)}`}>
                              {n.status === 'completed' ? '已完成' : n.status === 'active' ? '进行中' : n.status === 'expiring' ? '即将到期' : '未开始'}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-xs">{n.action_required}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-sm mb-3">案件统计</h3>
                <div className="border border-gray-200 rounded-lg p-4 space-y-3">
                  {[
                    { label: '文书数量', value: documents.length },
                    { label: '程序节点', value: nodes.length },
                    { label: '已完成节点', value: nodes.filter(n => n.status === 'completed').length, color: 'text-green-600' },
                    { label: '待办行动项', value: nodes.filter(n => n.status === 'active').length, color: 'text-red-600' },
                  ].map(item => (
                    <div key={item.label} className="flex justify-between text-sm">
                      <span className="text-gray-500">{item.label}</span>
                      <span className={`font-semibold ${item.color || ''}`}>{item.value}</span>
                    </div>
                  ))}
                  <div>
                    <div className="text-xs text-gray-500 mb-1">案件进度</div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-400 to-indigo-600 rounded-full"
                        style={{ width: `${nodes.length > 0 ? (nodes.filter(n => n.status === 'completed').length / nodes.length * 100) : 0}%` }}
                      />
                    </div>
                    <div className="text-right text-xs text-gray-400 mt-0.5">
                      {nodes.length > 0 ? Math.round(nodes.filter(n => n.status === 'completed').length / nodes.length * 100) : 0}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  {['全部', '法院文书', '我方文书', '证据材料'].map(f => (
                    <button key={f} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${f === '全部' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                      {f}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <label className="btn btn-outline text-xs flex items-center gap-1.5 cursor-pointer">
                    <Upload size={12} />
                    上传PDF
                    <input
                      type="file"
                      accept=".pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file)
                      }}
                      disabled={uploading}
                    />
                  </label>
                  <button
                    onClick={() => setShowDocModal(true)}
                    className="btn bg-indigo-600 text-white text-xs flex items-center gap-1.5 hover:bg-indigo-700"
                  >
                    <Plus size={12} /> 录入文书
                  </button>
                </div>
              </div>

              {/* Modal for adding document */}
              {showDocModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold">录入文书</h3>
                      <button onClick={() => setShowDocModal(false)} className="p-1 hover:bg-gray-100 rounded">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">文书类型</label>
                        <select
                          value={newDoc.type}
                          onChange={e => setNewDoc({ ...newDoc, type: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          <option value="">请选择</option>
                          <option value="受理通知书">受理通知书</option>
                          <option value="起诉状副本">起诉状副本</option>
                          <option value="答辩状">答辩状</option>
                          <option value="举证通知书">举证通知书</option>
                          <option value="开庭传票">开庭传票</option>
                          <option value="判决书">判决书</option>
                          <option value="裁定书">裁定书</option>
                          <option value="调解书">调解书</option>
                          <option value="证据清单">证据清单</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">日期</label>
                        <input
                          type="date"
                          value={newDoc.date}
                          onChange={e => setNewDoc({ ...newDoc, date: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">内容</label>
                        <textarea
                          value={newDoc.content}
                          onChange={e => setNewDoc({ ...newDoc, content: e.target.value })}
                          rows={4}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                      <button
                        onClick={() => setShowDocModal(false)}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                      >
                        取消
                      </button>
                      <button
                        onClick={handleCreateDocument}
                        disabled={!newDoc.type || !newDoc.date}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
                      >
                        保存
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">文书名称</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">类型</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">来源</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">日期</th>
                    <th className="text-center py-2 px-3 font-medium text-gray-500">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-400">暂无文书，点击"录入文书"添加</td>
                    </tr>
                  ) : (
                    documents.map(d => (
                      <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium">{d.doc_type}</td>
                        <td className="py-2 px-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            d.source === 'court' ? 'bg-blue-100 text-blue-700' :
                            d.source === 'our_side' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {d.source === 'court' ? '法院文书' : d.source === 'our_side' ? '我方文书' : '其他'}
                          </span>
                        </td>
                        <td className="py-2 px-3 text-gray-500 text-xs">
                          {d.source === 'court' ? '法院送达' : d.source === 'our_side' ? '我方提交' : '其他'}
                        </td>
                        <td className="py-2 px-3 text-gray-600">{d.doc_date || '—'}</td>
                        <td className="py-2 px-3 text-center">
                          {d.file_url ? (
                            <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-gray-200 rounded">
                              <FileText size={14} className="text-gray-500" />
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">无附件</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <h3 className="font-semibold text-sm mb-4">案件时间线</h3>
              {timeline.length === 0 ? (
                <div className="text-center py-8 text-gray-400">暂无时间线事件</div>
              ) : (
                <div className="relative pl-8">
                  <div className="absolute left-[15px] top-2 bottom-2 w-0.5 bg-gray-200" />
                  {timeline.map((event) => (
                    <div key={event.id} className="relative mb-6 last:mb-0">
                      <div className={`absolute -left-8 top-1 w-3 h-3 rounded-full border-2 ${
                        event.event_type === 'action_taken' ? 'bg-green-500 border-green-300' :
                        event.event_type === 'doc_received' ? 'bg-indigo-500 border-indigo-300' :
                        'bg-blue-500 border-blue-300'
                      }`} />
                      <div className="text-xs text-gray-400 mb-0.5">{event.event_date}</div>
                      <div className="font-medium text-sm">{event.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{event.description}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Nodes Tab */}
          {activeTab === 'nodes' && (
            <div>
              <h3 className="font-semibold text-sm mb-4">程序节点追踪</h3>
              {nodes.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  暂无程序节点
                  {caseData.filing_date && (
                    <button
                      onClick={() => navigate(`/cases/${id}/edit`)}
                      className="ml-2 text-indigo-600 hover:underline"
                    >
                      请编辑案件补充立案日期
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {nodes.map(n => (
                    <div key={n.id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                      n.status === 'completed' ? 'bg-green-50 border-green-200' :
                      n.status === 'active' ? 'bg-red-50 border-red-200' :
                      'bg-gray-50 border-gray-200'
                    }`}>
                      <span className="text-base">
                        {n.status === 'completed' ? '✅' : n.status === 'active' ? '🔴' : '⏳'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{n.node_name}</div>
                        <div className="text-xs text-gray-500">
                          {n.start_date} → {n.end_date} · {n.action_required}
                        </div>
                      </div>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColor(n.status)}`}>
                        {n.status === 'completed' ? '已完成' : n.status === 'active' ? '进行中' : n.status === 'expiring' ? '即将到期' : '未开始'}
                      </span>
                      {n.status === 'active' && (
                        <button
                          onClick={() => handleNodeComplete(n.id)}
                          className="ml-2 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          标记完成
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Analysis Tab */}
          {activeTab === 'analysis' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-sm">原告视角分析</h3>
                <button className="btn btn-outline text-xs flex items-center gap-1">
                  切换视角 ▾
                </button>
              </div>
              {analysis ? (
                <div className="border border-gray-200 rounded-lg p-5 prose prose-sm max-w-none">
                  {analysis.content.split('\n').map((line, i) => {
                    if (line.startsWith('## ')) return <h3 key={i} className="text-base font-bold mt-4 mb-2">{line.replace('## ', '')}</h3>
                    if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-semibold mt-3 mb-1">{line.replace('### ', '')}</h4>
                    if (line.startsWith('- [x]')) return <div key={i} className="flex items-center gap-2 text-sm text-green-700"><CheckSquare size={14} /> {line.replace('- [x] ', '')}</div>
                    if (line.startsWith('- [ ]')) return <div key={i} className="flex items-center gap-2 text-sm text-gray-700"><div className="w-3.5 h-3.5 border border-gray-400 rounded" /> {line.replace('- [ ] ', '')}</div>
                    if (line.trim()) return <p key={i} className="text-sm text-gray-700">{line}</p>
                    return <div key={i} className="h-2" />
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">暂无分析报告</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
